import { useState, useEffect, useRef, forwardRef } from 'react'
import { CurrencyDollar } from '@phosphor-icons/react'

// Supported currencies
type SupportedCurrency = 'USD' | 'BRL' | 'EUR' | 'GBP' | 'CNY'

interface MoneyInputProps {
  value: number | undefined
  onChange: (value: number) => void
  currency?: SupportedCurrency
  placeholder?: string
  disabled?: boolean
  className?: string
  size?: 'sm' | 'md' | 'lg'
  showCurrencySymbol?: boolean
  min?: number
  max?: number
  id?: string
  name?: string
  onBlur?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  error?: boolean
}

/**
 * Componente de input monetário com formatação automática
 * - Aceita apenas números
 * - Formata automaticamente com separadores de milhar
 * - Suporta USD e BRL
 * - Exibe símbolo da moeda
 */
const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(({
  value,
  onChange,
  currency = 'USD',
  placeholder,
  disabled = false,
  className = '',
  size = 'md',
  showCurrencySymbol = true,
  min,
  max,
  id,
  name,
  onBlur,
  onKeyDown,
  error = false
}, ref) => {
  const [displayValue, setDisplayValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Configurações por moeda
  const currencyConfig: Record<SupportedCurrency, {
    symbol: string
    locale: string
    decimalSeparator: string
    thousandSeparator: string
  }> = {
    USD: {
      symbol: '$',
      locale: 'en-US',
      decimalSeparator: '.',
      thousandSeparator: ','
    },
    BRL: {
      symbol: 'R$',
      locale: 'pt-BR',
      decimalSeparator: ',',
      thousandSeparator: '.'
    },
    EUR: {
      symbol: '€',
      locale: 'de-DE',
      decimalSeparator: ',',
      thousandSeparator: '.'
    },
    GBP: {
      symbol: '£',
      locale: 'en-GB',
      decimalSeparator: '.',
      thousandSeparator: ','
    },
    CNY: {
      symbol: '¥',
      locale: 'zh-CN',
      decimalSeparator: '.',
      thousandSeparator: ','
    }
  }

  const config = currencyConfig[currency]

  // Formatar número para exibição
  const formatForDisplay = (num: number | undefined): string => {
    if (num === undefined || num === null || isNaN(num)) return ''

    return new Intl.NumberFormat(config.locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num)
  }

  // Parsear string para número
  const parseToNumber = (str: string): number => {
    if (!str) return 0

    // Remove tudo exceto números, vírgula e ponto
    let cleaned = str.replace(/[^\d.,]/g, '')

    // Normaliza para formato numérico (ponto como decimal)
    if (config.decimalSeparator === ',') {
      // Moedas que usam vírgula como decimal (BRL, EUR)
      cleaned = cleaned.replace(/\./g, '').replace(',', '.')
    } else {
      // Moedas que usam ponto como decimal (USD, GBP, CNY)
      cleaned = cleaned.replace(/,/g, '')
    }

    const num = parseFloat(cleaned)
    return isNaN(num) ? 0 : num
  }

  // Atualizar display quando value mudar externamente
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatForDisplay(value))
    }
  }, [value, isFocused])

  // Lidar com mudança de input
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value

    // Permitir apenas números e separadores baseado na moeda
    const decimalSep = config.decimalSeparator
    const allowedChars = decimalSep === ',' ? /[^\d,]/g : /[^\d.]/g
    let cleaned = rawValue.replace(allowedChars, '')

    // Garantir apenas um separador decimal
    const parts = cleaned.split(decimalSep)
    if (parts.length > 2) {
      cleaned = parts[0] + decimalSep + parts.slice(1).join('')
    }

    // Limitar casas decimais a 2
    if (parts.length === 2 && parts[1].length > 2) {
      cleaned = parts[0] + decimalSep + parts[1].slice(0, 2)
    }

    setDisplayValue(cleaned)

    const numValue = parseToNumber(cleaned)

    // Validar min/max
    let finalValue = numValue
    if (min !== undefined && numValue < min) finalValue = min
    if (max !== undefined && numValue > max) finalValue = max

    onChange(finalValue)
  }

  // Formatar ao perder foco
  const handleBlur = () => {
    setIsFocused(false)
    setDisplayValue(formatForDisplay(value))
    onBlur?.()
  }

  // Ao focar, mostrar valor sem formatação e selecionar tudo
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    if (value && value > 0) {
      // Mostrar valor com decimais mas sem separadores de milhar
      const decimalSep = currency === 'BRL' ? ',' : '.'
      setDisplayValue(value.toFixed(2).replace('.', decimalSep))
    }
    // Selecionar todo o texto após um pequeno delay para garantir que o valor foi atualizado
    requestAnimationFrame(() => {
      e.target.select()
    })
  }

  // Tamanhos
  const sizeClasses = {
    sm: 'h-8 text-sm px-2',
    md: 'h-10 text-base px-3',
    lg: 'h-12 text-lg px-4'
  }

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24
  }

  return (
    <div className={`relative ${className}`}>
      {showCurrencySymbol && (
        <div className={`
          absolute left-0 top-0 bottom-0 flex items-center justify-center
          ${size === 'sm' ? 'w-8' : size === 'md' ? 'w-10' : 'w-12'}
          ${disabled ? 'text-gray-400' : 'text-gray-500'}
          pointer-events-none
        `}>
          {currency === 'USD' ? (
            <CurrencyDollar size={iconSizes[size]} weight="bold" />
          ) : (
            <span className="font-semibold text-sm">R$</span>
          )}
        </div>
      )}

      <input
        ref={ref || inputRef}
        type="text"
        inputMode="decimal"
        id={id}
        name={name}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        disabled={disabled}
        placeholder={placeholder || `0${config.decimalSeparator}00`}
        className={`
          w-full rounded-lg border bg-white transition-all
          ${sizeClasses[size]}
          ${showCurrencySymbol ? (size === 'sm' ? 'pl-8' : size === 'md' ? 'pl-10' : 'pl-12') : ''}
          ${error
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
            : 'border-gray-300 focus:border-emerald-500 focus:ring-emerald-500/20'
          }
          ${disabled
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
            : 'hover:border-gray-400'
          }
          focus:outline-none focus:ring-2
          text-right font-mono
        `}
      />
    </div>
  )
})

MoneyInput.displayName = 'MoneyInput'

export default MoneyInput

// Locale mapping for currencies
const CURRENCY_LOCALES: Record<SupportedCurrency, string> = {
  USD: 'en-US',
  BRL: 'pt-BR',
  EUR: 'de-DE',
  GBP: 'en-GB',
  CNY: 'zh-CN',
}

// Componente auxiliar para exibir valor formatado (read-only)
interface MoneyDisplayProps {
  value: number
  currency?: SupportedCurrency
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  showSymbol?: boolean
}

export function MoneyDisplay({
  value,
  currency = 'USD',
  size = 'md',
  className = '',
  showSymbol = true
}: MoneyDisplayProps) {
  const locale = CURRENCY_LOCALES[currency] || 'en-US'
  const formatted = new Intl.NumberFormat(locale, {
    style: showSymbol ? 'currency' : 'decimal',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)

  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-2xl font-bold'
  }

  return (
    <span className={`font-mono ${sizeClasses[size]} ${className}`}>
      {formatted}
    </span>
  )
}

// Hook para conversão de moedas
export function useMoneyFormat(currency: SupportedCurrency = 'USD') {
  const locale = CURRENCY_LOCALES[currency] || 'en-US'

  const format = (value: number): string => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
  }

  const formatCompact = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`
    }
    return format(value)
  }

  return { format, formatCompact }
}
